import ChevronDownSmall from "@DSG/Icons/ChevronDownSmall";
import Reorder from "@DSG/MobiusOverrides/Icons/RefreshCw";
import Popover from "@DSG/MobiusOverrides/Popover/Popover";
import colorPallete, { bodyFonts, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetDrawerIsOpen";
import { getFulfillmentLabel, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    fulfillmentLabel: getFulfillmentLabel(state),
    isDrawerOpen: state.components.addressDrawer.isOpen,
    isPunchOutSession: getIsPunchOutSession(state),
    session: state.context.session,
    accountSettings: getSettingsCollection(state).accountSettings,
    changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomer")?.url,
    shipToState: getShipToState(state, state.context.session.shipToId),
    currentUserIsGuest: getCurrentUserIsGuest(state),
    signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
});

const mapDispatchToProps = {
    setDrawerIsOpen,
    setFulfillmentMethod,
    updatePickUpWarehouse,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasShellContext &
    HasHistory &
    HasToasterContext;

export interface HeaderReorderLinksStyles {
    reorderLinkWrapper: InjectableCss;
    shipToAddressWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    myListTitle?: TypographyPresentationProps;
    reorderTitle?: TypographyPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    drawerInPunchOut?: DrawerPresentationProps;
    pickUpLocation?: PickUpLocationStyles;
    popoverContainer: GridContainerProps;
    popoverGridItem: GridItemProps;
}
export interface WarehouseAddressInfoDisplayStyles {
    wrapper?: InjectableCss;
    nameText?: TypographyProps;
    address?: AddressInfoDisplayStyles;
}
export interface PickUpLocationStyles {
    wrapper?: InjectableCss;
    headerText?: TypographyProps;
    changeLink?: LinkPresentationProps;
    warehouseAddress?: WarehouseAddressInfoDisplayStyles;
    findLocationModal?: FindLocationModalStyles;
}

const baseDrawerStyles: DrawerPresentationProps = {
    closeButtonProps: {
        shape: "pill",
        buttonType: "solid",
        color: "common.background",
        size: 36,
    },
    closeButtonIconProps: {
        src: "X",
        size: 24,
    },
    cssOverrides: {
        drawerContent: css`
            height: 100%;
            margin: 0 45px;
            overflow-y: inherit;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            max-height: 500px;
            overflow-x: inherit;
            overflow-y: inherit;
        `,
        drawerTitle: css`
            background: ${getColor("common.background")};
        `,
    },
    headlineTypographyProps: {
        color: "text.main",
    },
};

export const headerReorderLinksStyles: HeaderReorderLinksStyles = {
    reorderLinkWrapper: {
        css: css`
            margin: 0 16px;
        `,
    },
    shipToAddressWrapper: {
        css: css`
            display: inline-flex;
            width: 100%;
        `,
    },
    titleClickable: {
        css: css`
            display: block;
            > span {
                display: flex;
                align-items: center;
                gap: 12px;
            }

            &:hover {
                [data-test-selector="header_reorderLinks"] {
                    color: ${colorPallete.primaryRed500};

                    [class*="IconWrapper"] {
                        color: ${colorPallete.primaryRed500}
                    }
                }
            }
        `,
    },
    titleIcon: {
        size: 22,
    },
    myListTitle: {
        size: labelFonts.label1Regular.fontSize,
        lineHeight: labelFonts.label1Regular.lineHeight,
        css: css`
            display: block;
        `,
    },
    reorderTitle: {
        size: labelFonts.label1SemiBold.fontSize,
        lineHeight: labelFonts.label1SemiBold.lineHeight,
        weight: labelFonts.label1SemiBold.fontWeight,
        css: css`
            display: flex;
            align-items: center;

            span {
                margin-left: 4px;
            }
        `,
    },
    drawer: baseDrawerStyles,
    drawerInShell: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                height: auto;
                overflow-x: inherit;
                overflow-y: inherit;
            `,
        },
    },
    drawerInPunchOut: {
        ...baseDrawerStyles,
        cssOverrides: {
            ...baseDrawerStyles.cssOverrides,
            drawerBody: css`
                background: ${getColor("common.background")};
                overflow-x: inherit;
                overflow-y: show;
            `,
        },
    },
    popoverContainer: {
        offsetCss: css`
            width: 100% !important;
            margin: 0;
        `,
    },
    popoverGridItem: {
        css: css`
            width: 100%;
            flex-basis: 100%;
            max-width: unset;

            [class*="UnorderedListStyle"],
            .list-wrapper {
                padding: 0;
                list-style: none;
                border: 1px solid ${colorPallete.grey300};
                border-top: 0;
                border-bottom-left-radius: 8px;
                border-bottom-right-radius: 8px;
                text-transform: initial;

                li,
                .links-wrapper {
                    padding: 8px;
                    padding-left: 16px;
                    cursor: pointer;

                    &:hover {
                        background: ${colorPallete.grey200};
                    }

                    &,
                    & a {
                        display: block;
                        font-size: ${bodyFonts.body2RegularSm.fontSize};
                        line-height: ${bodyFonts.body2RegularSm.lineHeight};
                    }
                }
            }
        `,
    },
};

const wapperStyles = css`
    display: none;
`

interface OwnProps extends WidgetProps {
    fields: {};
}

const HeaderReorderLinks: FC<Props> = ({ id, signInPageUrl, session }) => {

    const list = React.createRef();
    const popover = React.createRef<any>();
    const reorderLinkRef = React.createRef<HTMLAnchorElement>();

    useEffect(() => {
        const popoverGridItems = document.querySelectorAll('.popoverGridItem li');
        Array.from(popoverGridItems).forEach(item => {
            item.addEventListener('click', () => {
                popover.current && (popover.current! as any).closePopover();
            })
        })
       
        const handleKeyDown = (e: KeyboardEvent) => {
            const triggerElement =  document.querySelector('.header-reorder #header_reorderLinks') as HTMLElement;
          
            if (e.key === 'Escape' && triggerElement.classList.contains('myListOpen')) {
                triggerElement.setAttribute('aria-expanded', 'false');
                setTimeout(() => {
                    triggerElement.classList.remove('myListOpen');
                    triggerElement?.focus();
                }, 100);  
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        // Cleanup event listener when component is unmounted
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };

    },[]);
    

    const setFocusOnPopover = () => {
        const popoverGridItems = document.querySelectorAll('.popoverGridItem .links-wrapper');
        const triggerElement =  document.querySelector('.header-reorder #header_reorderLinks') as HTMLElement;
        triggerElement.classList.add('myListOpen');  
        triggerElement.setAttribute('aria-expanded', 'true');
        setTimeout(() => {
            if (popoverGridItems.length > 0) {
                const firstItem = popoverGridItems[0]; 
                const firstLink = firstItem.querySelector('a');
    
                if (firstLink) {
                    firstLink.focus();
                }
            }
        }, 100);
    }
    

    const styles = headerReorderLinksStyles;
    const reorderLinks = (
        <Clickable
            {...styles.titleClickable}
            data-test-selector="header_MyListReorderLinks"
            id="header_reorderLinks"
            ref={reorderLinkRef} 
            href={session.isAuthenticated ? '' : signInPageUrl}
            {...(!session.isAuthenticated ? 
                {'aria-label' : 'Clicking on the link will navigate to the login page', 'aria-describedby': 'myListInfo'} 
                : ''
            )}
        >
            <Typography>
                <Icon src={Reorder} />
                <Typography>
                    <Typography {...styles.myListTitle} data-test-selector="header_MyListLinks">
                        {translate("My Lists")}&nbsp;
                    </Typography>
                    <Typography
                        {...styles.reorderTitle}
                        className={`${session.isAuthenticated ? "reorder-active" : ""}`}
                        data-test-selector="header_reorderLinks"
                    >
                        {translate("Reorder")}
                        <Icon src={ChevronDownSmall} />
                    </Typography>
                </Typography>
            </Typography>
        </Clickable>
    );
    return (
        <>
            {!session.isAuthenticated && (
                <StyledWrapper {...styles.reorderLinkWrapper}>
                    {reorderLinks}
                    <VisuallyHidden id="myListInfo">
                        {translate('My Lists dropdown feature is available only for authenticated users.')}
                    </VisuallyHidden>
                </StyledWrapper>
            )}
            {session.isAuthenticated && (
                <Popover
                    controlsId="header_MyListReorderLinks-popover"
                    ref={popover}
                    insideRefs={[list]}
                    popoverTrigger={reorderLinks}
                    positionFunction={(element: React.RefObject<HTMLUListElement>) => {
                        return { position: "absolute", top: "calc(100% + 20px)", width: "194px", margin: "0", left: "-40px", padding: "0"
                        };
                    }}
                    contentBodyProps={
                        {
                            ref: list,
                        } as any
                    }
                    onOpen={setFocusOnPopover}
                    className="popover"
                >
                   <GridContainer {...styles.popoverContainer}>
                        <GridItem {...styles.popoverGridItem} className="popoverGridItem verticalLinks">
                            <Zone contentId={id} zoneName="DSGMyListReorderLinks" />
                        </GridItem>
                    </GridContainer>
                </Popover>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(HeaderReorderLinks)),
    definition: {
        displayName: "Header Reorder Links",
        icon: "location-dot",
        group: "Header",
    },
};

export default widgetModule;
